import React, { useState } from "react";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { X, FileCheck } from "lucide-react";
import { useArchivalMode } from "@/contexts/ArchivalModeContext";

interface CAROClauseVIIIChecklistModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (responses: Record<string, string>) => void;
  initialResponses?: Record<string, string>;
}

interface Question {
  id: string;
  text: string;
  dependsOn?: string;
  showIf?: string;
}

const questions: Question[] = [
  {
    id: "q1",
    text: "Has any income been surrendered or disclosed as income during the year in tax assessments under the Income Tax Act?"
  },
  {
    id: "q2",
    text: "Has it been recorded in the books of account?",
    dependsOn: "q1",
    showIf: "yes"
  }
];

export default function CAROClauseVIIIChecklistModal({ 
  isOpen, 
  onClose, 
  onSave,
  initialResponses = {}
}: CAROClauseVIIIChecklistModalProps) {
  const { isArchivalMode } = useArchivalMode();
  const [responses, setResponses] = useState<Record<string, string>>(initialResponses);

  // Update responses when initialResponses changes (when modal opens with new data)
  React.useEffect(() => {
    if (isOpen && initialResponses) {
      setResponses(initialResponses);
    }
  }, [isOpen, initialResponses]);

  const handleResponseChange = (questionId: string, value: string) => {
    setResponses(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const shouldShowQuestion = (question: Question): boolean => {
    if (!question.dependsOn) return true;
    
    const dependentResponse = responses[question.dependsOn];
    return dependentResponse === question.showIf;
  };

  const handleSave = () => {
    onSave(responses);
    onClose();
  };

  const handleClose = () => {
    setResponses({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <Card className="w-full max-w-4xl max-h-[90vh] border-white/10 bg-black/80 text-white shadow-2xl overflow-hidden">
        <CardHeader className="flex flex-row items-center justify-between border-b border-white/10">
          <div className="flex items-center gap-3">
            <FileCheck className="h-5 w-5 text-[#4da3ff]" />
            <CardTitle className="text-lg">
              CARO Clause (viii) - Undisclosed Income
            </CardTitle>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClose}
            className="text-white/60 hover:text-white"
          >
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>
        
        <CardContent className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          <div className="space-y-6">
            {questions.map((question, index) => {
              const isVisible = shouldShowQuestion(question);
              
              if (!isVisible) return null;

              return (
                <div key={question.id} className="space-y-3">
                  <p className="text-sm text-white/90">
                    {question.text}
                  </p>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name={question.id}
                        value="yes"
                        checked={responses[question.id] === "yes"}
                        onChange={(e) => handleResponseChange(question.id, e.target.value)}
                        className="w-4 h-4 text-[#4da3ff] bg-white/10 border-white/20 focus:ring-[#4da3ff]"
                      />
                      <span className="text-sm text-white/80">Yes</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name={question.id}
                        value="no"
                        checked={responses[question.id] === "no"}
                        onChange={(e) => handleResponseChange(question.id, e.target.value)}
                        className="w-4 h-4 text-[#4da3ff] bg-white/10 border-white/20 focus:ring-[#4da3ff]"
                      />
                      <span className="text-sm text-white/80">No</span>
                    </label>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>

        <div className="flex justify-end gap-3 p-6 border-t border-white/10">
          <Button
            variant="outline"
            onClick={handleClose}
            className="border-white/20 text-white/80 hover:bg-white/10"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            className="bg-[#4da3ff] text-white hover:bg-[#4da3ff]/80"
            disabled={isArchivalMode}
          >
            Save Responses
          </Button>
        </div>
      </Card>
    </div>
  );
}
